import { cn } from '@/lib/utils/cn.js';
import type { ElementType, ComponentPropsWithoutRef } from 'react';

type CardOwnProps<E extends ElementType = 'div'> = {
  as?: E;
  featured?: boolean;
  hoverable?: boolean;
  className?: string;
};

type CardProps<E extends ElementType = 'div'> = CardOwnProps<E> &
  Omit<ComponentPropsWithoutRef<E>, keyof CardOwnProps<E>>;

export function Card<E extends ElementType = 'div'>({
  as,
  featured = false,
  hoverable = false,
  className,
  children,
  ...rest
}: CardProps<E>) {
  const As = (as ?? 'div') as ElementType;
  return (
    <As
      className={cn(
        // border width stays (callers override the color, e.g. info cards);
        // default is transparent — flat minimal surfaces, no outline.
        'rounded-xl border bg-[var(--bg-raised)]',
        featured
          ? 'border-[var(--primary)] shadow-[0_0_24px_var(--primary-glow)]'
          : 'border-transparent',
        hoverable && 'transition-all duration-200 hover:bg-[var(--bg-elevated)]',
        className,
      )}
      {...rest}
    >
      {children}
    </As>
  );
}
